import React, { useEffect, useState } from "react";
import { FaBars, FaShoppingBag, FaUsers, FaChevronLeft, FaChevronRight, FaDownload, FaChevronDown, FaChevronUp, FaChartBar } from "react-icons/fa";
import jsPDF from "jspdf";
import OrderWorkflowManager from "../../components/OrderWorkflow/OrderWorkflowManager";
import AnalyticsDashboard from "../../components/Analytics/AnalyticsDashboard";
import "./AdminPanel.css";
import base_url from "../../api";

const OrdersWithMeasurements = () => {
  const [orders, setOrders] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [visibleMeasurements, setVisibleMeasurements] = useState(new Set()); // Start collapsed, user can expand
  const [expandedMeasurementDetails, setExpandedMeasurementDetails] = useState(new Set()); // Track which measurement details are expanded
  const [activeTab, setActiveTab] = useState("analytics");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setError("");
      const [ordersRes, submissionsRes, usersRes] = await Promise.all([
        fetch(`${base_url}/orders/all`),
        fetch(`${base_url}/measurements/submissions/all`),
        fetch(`${base_url}/users/all`).catch(() => ({ ok: false }))
      ]);

      const ordersData = ordersRes.ok ? await ordersRes.json() : null;
      const submissionsData = submissionsRes.ok ? await submissionsRes.json() : null;
      const usersData = usersRes.ok ? await usersRes.json() : null;

      if (ordersData?.success) {
        setOrders(ordersData.orders || []);
      } else if (ordersData && ordersData.error) {
        setError(ordersData.error);
      }

      if (submissionsData?.success) {
        setSubmissions(submissionsData.submissions || []);
      } else if (submissionsData && submissionsData.error) {
        setError((prev) => prev || submissionsData.error);
      }

      if (usersData?.success) {
        setUsers(usersData.users || []);
      } else if (usersData && usersData.error) {
        console.error("Users fetch error:", usersData.error);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to load orders or measurements");
    } finally {
      setLoading(false);
    }
  };

  // Get measurements for a specific order
  const getMeasurementsForOrder = (order) => {
    const key = order.orderId || order.orderNo || order.id;
    if (!key) return [];
    return submissions.filter((s) => String(s.orderNo) === String(key));
  };

  const toggleOrder = (orderKey) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(orderKey)) {
        next.delete(orderKey);
      } else {
        next.add(orderKey);
      }
      return next;
    });
  };

  const toggleMeasurements = (measurementKey) => {
    setVisibleMeasurements((prev) => {
      const next = new Set(prev);
      if (next.has(measurementKey)) {
        next.delete(measurementKey);
      } else {
        next.add(measurementKey);
      }
      return next;
    });
  };

  const toggleMeasurementDetails = (measurementDetailKey) => {
    setExpandedMeasurementDetails((prev) => {
      const next = new Set(prev);
      if (next.has(measurementDetailKey)) {
        next.delete(measurementDetailKey);
      } else {
        next.add(measurementDetailKey);
      }
      return next;
    });
  };

  // Helper function to convert image URL to base64
  const imageToBase64 = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        try {
          const base64 = canvas.toDataURL("image/jpeg", 0.8);
          resolve(base64);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  const generatePDF = async (order, measurements) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let yPos = margin;

    // Header
    doc.setFontSize(20);
    doc.setFont(undefined, "bold");
    doc.text("Measurement Details Report", pageWidth / 2, yPos, { align: "center" });
    yPos += 10;

    doc.setFontSize(12);
    doc.setFont(undefined, "normal");
    doc.text(`Order ID: ${order.orderId || order.id || order.orderNo}`, margin, yPos);
    yPos += 7;
    doc.text(`Order Date: ${new Date(order.order_date || order.orderDate).toLocaleString()}`, margin, yPos);
    yPos += 5;

    // Measurements - each on a separate page
    for (let idx = 0; idx < measurements.length; idx++) {
      const measurement = measurements[idx];

      // Start each measurement on a new page (except the first one which is already on the first page)
      if (idx > 0) {
        doc.addPage();
        yPos = margin;
      }

      doc.setFontSize(16);
      doc.setFont(undefined, "bold");
      doc.text(`Measurement #${idx + 1}`, margin, yPos);
      yPos += 8;

      doc.setFontSize(11);
      doc.setFont(undefined, "normal");
      doc.text(`Recipient: ${measurement.recipientName || "N/A"}`, margin, yPos);
      yPos += 6;
      doc.text(`Email: ${measurement.recipientEmail}`, margin, yPos);
      yPos += 6;
      doc.text(`Status: ${measurement.status}`, margin, yPos);
      yPos += 6;
      if (measurement.submittedAt) {
        doc.text(`Submitted: ${new Date(measurement.submittedAt).toLocaleString()}`, margin, yPos);
        yPos += 6;
      }

      // Look details
      if (measurement.look) {
        yPos += 3;
        doc.setFontSize(12);
        doc.setFont(undefined, "bold");
        doc.text("Look Details:", margin, yPos);
        yPos += 7;
        doc.setFontSize(11);
        doc.setFont(undefined, "normal");
        doc.text(`Look: ${measurement.look.lookTitle || measurement.look.lookName || "N/A"}`, margin, yPos);
        yPos += 6;
        doc.text(`Kurta: ${measurement.look.kurtaColor} (${measurement.look.kurtaFabric})`, margin, yPos);
        yPos += 6;
        doc.text(`Pajama: ${measurement.look.pajamaColor} (${measurement.look.pajamaFabric})`, margin, yPos);
        yPos += 6;
        doc.text(`Shawl: ${measurement.look.shawl || "No"}`, margin, yPos);
        yPos += 6;

        // Add look image if available
        if (measurement.look.image) {
          try {
            const imageBase64 = await imageToBase64(measurement.look.image);
            const imgWidth = 60;
            const imgHeight = (imgWidth * 0.75); // Maintain aspect ratio

            // Check if image fits on current page
            if (yPos + imgHeight > 270) {
              doc.addPage();
              yPos = margin;
            }

            doc.addImage(imageBase64, "JPEG", margin, yPos, imgWidth, imgHeight);
            yPos += imgHeight + 5;
          } catch (err) {
            console.error("Error loading image:", err);
            // Continue without image if it fails to load
          }
        }
      }

      // Measurements table
      yPos += 3;
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Measurements:", margin, yPos);
      yPos += 7;

      doc.setFontSize(10);
      doc.setFont(undefined, "normal");

      const tableStartY = yPos;
      let tableY = tableStartY;

      // Table header
      doc.setFont(undefined, "bold");
      doc.text("Measurement", margin, tableY);
      doc.text("Value", pageWidth - margin - 30, tableY, { align: "right" });
      tableY += 5;
      doc.line(margin, tableY, pageWidth - margin, tableY);
      tableY += 3;

      // Table rows
      doc.setFont(undefined, "normal");
      (measurement.measurements || []).forEach((m) => {
        if (tableY > 270) {
          doc.addPage();
          tableY = margin + 10;
        }
        doc.text(m.label || `Measurement ${m.key}`, margin, tableY);
        doc.text(m.value, pageWidth - margin - 30, tableY, { align: "right" });
        tableY += 6;
      });

      yPos = tableY + 10;
    }

    // Footer
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        `Page ${i} of ${totalPages}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }

    // Save PDF
    const fileName = `Order_${order.orderId || order.id || order.orderNo}_Measurements.pdf`;
    doc.save(fileName);
  };

  const renderOrdersView = () => {
    if (loading) return <div className="loader">Loading orders...</div>;
    if (error) return <div className="error">{error}</div>;

    return (
      <div className="orders-page">
        <div className="page-header">
          <h2 className="page-title">📦 Orders & Measurements</h2>
          <div className="page-subtitle">Manage orders and track measurement submissions</div>
        </div>

        {orders.map((order, index) => {
          const matchedMeasurements = getMeasurementsForOrder(order);
          const orderKey = order.orderId || order.orderNo || order.id || index;
          const isExpanded = expandedOrders.has(orderKey);

          return (
            <div key={index} className={`order-card ${isExpanded ? "expanded" : ""}`}>
              <div
                className="order-header"
                onClick={() => toggleOrder(orderKey)}
                style={{ cursor: "pointer" }}
              >
                <div>
                  <strong>Order ID:</strong> {order.orderId || order.id}
                  <div className="muted">
                    {new Date(order.order_date || order.orderDate).toLocaleString()}
                  </div>
                </div>

                <div>
                  <strong>Total:</strong>CAD - C$ {order.total}
                  <div className="muted" style={{ marginTop: "4px" }}>
                    {isExpanded ? "▼" : "▶"} {matchedMeasurements.length} measurement{matchedMeasurements.length !== 1 ? "s" : ""}
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="order-details-content">
                  {matchedMeasurements.length > 0 ? (
                    <div className="measurements-list">
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                        <h3 style={{ margin: 0, fontSize: "18px" }}>
                          📐 All Measurements ({matchedMeasurements.length})
                        </h3>
                        <button
                          className="download-pdf-btn"
                          onClick={async () => {
                            try {
                              await generatePDF(order, matchedMeasurements);
                            } catch (err) {
                              console.error("Error generating PDF:", err);
                              alert("Failed to generate PDF. Please try again.");
                            }
                          }}
                          title="Download PDF"
                        >
                          <FaDownload /> Download PDF
                        </button>
                      </div>
                      {matchedMeasurements.map((measurement, idx) => {
                        const measurementDetailKey = `measurement-detail-${idx}-${measurement.id || idx}`;
                        const isExpanded = expandedMeasurementDetails.has(measurementDetailKey);
                        
                        return (
                        <div key={measurement.id || idx} className="measurement-details">
                          <div 
                            className={`measurement-header drawer-header ${isExpanded ? 'expanded' : ''}`}
                            onClick={() => toggleMeasurementDetails(measurementDetailKey)}
                            style={{ cursor: "pointer" }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                              <h4 className="measurement-number">Measurement #{idx + 1}</h4>
                            </div>
                            <span className={`measurement-status-badge status-${measurement.status?.toLowerCase()}`}>
                              {measurement.status}
                            </span>
                          </div>

                          <div className={`measurement-drawer-content ${isExpanded ? 'expanded' : 'collapsed'}`}>
                            <div className="measurement-info-grid">
                            <div className="info-item">
                              <span className="info-label">Order No:</span>
                              <span className="info-value">{measurement.orderNo}</span>
                            </div>
                            <div className="info-item">
                              <span className="info-label">Recipient:</span>
                              <span className="info-value">{measurement.recipientName || "N/A"}</span>
                            </div>
                            <div className="info-item">
                              <span className="info-label">Email:</span>
                              <span className="info-value">{measurement.recipientEmail}</span>
                            </div>
                            {measurement.submittedAt && (
                              <div className="info-item">
                                <span className="info-label">Submitted:</span>
                                <span className="info-value">
                                  {new Date(measurement.submittedAt).toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>

                          {measurement.look && (
                            <div className="look-section">
                              <h5 className="look-section-title">👗 Look Details</h5>
                              <div className="look-content-wrapper">
                                {/* LEFT: Look Details */}
                                <div className="look-details-text">
                                  <h6 className="look-title">
                                    {measurement.look.lookTitle ||
                                      measurement.look.lookName ||
                                      "Look"}
                                  </h6>
                                  <div className="look-specs">
                                    <div className="look-spec-item">
                                      <span className="spec-label">Kurta:</span>
                                      <span className="spec-value">
                                        {measurement.look.kurtaColor} ({measurement.look.kurtaFabric})
                                      </span>
                                    </div>
                                    <div className="look-spec-item">
                                      <span className="spec-label">Pajama:</span>
                                      <span className="spec-value">
                                        {measurement.look.pajamaColor} ({measurement.look.pajamaFabric})
                                      </span>
                                    </div>
                                    <div className="look-spec-item">
                                      <span className="spec-label">Shawl:</span>
                                      <span className="spec-value">
                                        {measurement.look.shawl || "No"}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* RIGHT: Look Image */}
                                {measurement.look.image && (
                                  <div className="look-image-wrapper">
                                    <img
                                      src={measurement.look.image}
                                      alt="Look"
                                      className="look-image"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="measurements-table-section">
                            <div className="measurements-table-header">
                              <h5 className="measurements-table-title">📏 Measurements</h5>
                              <button
                                className="measurements-toggle-btn"
                                onClick={() => toggleMeasurements(`measurement-${idx}-${measurement.id || idx}`)}
                                type="button"
                              >
                                {visibleMeasurements.has(`measurement-${idx}-${measurement.id || idx}`) ? (
                                  <>
                                    <FaChevronUp /> Hide
                                  </>
                                ) : (
                                  <>
                                    <FaChevronDown /> Show
                                  </>
                                )}
                              </button>
                            </div>
                            <div 
                              className={`measurements-table-wrapper ${visibleMeasurements.has(`measurement-${idx}-${measurement.id || idx}`) ? 'expanded' : 'collapsed'}`}
                            >
                              <table className="measurements-table">
                                <tbody>
                                  {(measurement.measurements || []).map((m, i) => (
                                    <tr key={i} className="measurement-row">
                                      <td className="measurement-label">{m.label}</td>
                                      <td className="measurement-value">{m.value}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          </div>
                        </div>
                        );
                      })}
                        </div>
                      ) : (
                      <div className="no-measurements">No measurements submitted</div>
                )}

                  {/* Order Workflow Manager */}
                  <div style={{ marginTop: "30px" }}>
                    <OrderWorkflowManager
                      order={order}
                      orderId={order.orderId || order.id || order.orderNo}
                      onUpdate={() => {
                        // Refresh orders data after workflow update
                        fetchData();
                      }}
                    />
                  </div>
                    </div>
                  )}
                </div>
              );
      })}
            </div>
          );
        };

  const renderUsersView = () => {
    return (
        <div className="users-page">
          <h2>👥 Users Management</h2>
          {users.length === 0 ? (
            <div className="no-data">No users found</div>
          ) : (
            <div className="users-list">
              {users.map((user, index) => (
                <div key={user.id || index} className="user-card">
                  <div className="user-info">
                    <h3>{user.name || "N/A"}</h3>
                    <p className="muted">Email: {user.email}</p>
                    {user.phone && <p className="muted">Phone: {user.phone}</p>}
                    {user.created_at && (
                      <p className="muted">Joined: {new Date(user.created_at).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        );
  };

        return (
        <div className="admin-panel-container">
          {/* Sidebar */}
          <aside className={`admin-sidebar ${sidebarOpen ? "open" : "closed"}`}>
            <div className="sidebar-header">
              <h2>Admin Panel</h2>
              <button
                className="sidebar-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? <FaChevronLeft /> : <FaBars />}
              </button>
            </div>

            <nav className="sidebar-nav">
            <button
                className={`nav-item ${activeTab === "analytics" ? "active" : ""}`}
                onClick={() => setActiveTab("analytics")}
              >
                <FaChartBar className="nav-icon" />
                {sidebarOpen && <span>Analytics</span>}
                {sidebarOpen && <FaChevronRight className="nav-arrow" />}
              </button>
              <button
                className={`nav-item ${activeTab === "orders" ? "active" : ""}`}
                onClick={() => setActiveTab("orders")}
              >
                <FaShoppingBag className="nav-icon" />
                {sidebarOpen && <span>Orders</span>}
                {sidebarOpen && <FaChevronRight className="nav-arrow" />}
              </button>

              <button
                className={`nav-item ${activeTab === "users" ? "active" : ""}`}
                onClick={() => setActiveTab("users")}
              >
                <FaUsers className="nav-icon" />
                {sidebarOpen && <span>Users</span>}
                {sidebarOpen && <FaChevronRight className="nav-arrow" />}
              </button>

          
            </nav>
          </aside>

          {/* Main Content */}
          <main className={`admin-main-content ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}>
            {activeTab === "orders" && renderOrdersView()}
            {activeTab === "users" && renderUsersView()}
            {activeTab === "analytics" && (
              <AnalyticsDashboard 
                orders={orders} 
                users={users} 
                submissions={submissions}
                onNavigateToTab={setActiveTab}
              />
            )}
          </main>
        </div>
        );
};

        export default OrdersWithMeasurements;
